import { FormEvent, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { ApiError } from '../../../api/errors';
import { useAuth } from '../../../auth/AuthProvider';
import { clearFirstMachineActivationSession } from '../../../auth/first-machine-activation-session';
import { useFacilities } from '../../organization/api/useFacilities';
import { useCreateMachine, useSelectMachine } from '../api/useCreateMachine';
import type { CreateMachineInput, PossibleDuplicateDto } from '../types/machine';
import { isPossibleDuplicate } from '../types/machine';

type FieldErrors = Record<string, string>;
type MachineCreateVariant = 'standard' | 'first-machine';

interface MachineCreateFormProps {
  variant?: MachineCreateVariant;
  cancelTo?: string;
  onCancel?: () => void;
  afterCreatePath?: (machineId: string) => string;
}

const emptyForm = {
  machineName: '',
  machineType: '',
  assetId: '',
  manufacturer: '',
  model: '',
  location: '',
  productionArea: '',
  productionLine: '',
  aliases: '',
};

function clean(value: string): string | null {
  const trimmed = value.trim();
  return trimmed || null;
}

function newIdempotencyKey(): string {
  return typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `r42-web-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function apiFieldErrors(error: unknown): FieldErrors {
  if (!(error instanceof ApiError) || !error.details || typeof error.details !== 'object') return {};
  const fields = (error.details as Record<string, unknown>).field_errors;
  if (!fields || typeof fields !== 'object') return {};
  return Object.fromEntries(
    Object.entries(fields as Record<string, unknown>).map(([key, value]) => [key, String(value)]),
  );
}

export function MachineCreateForm({
  variant = 'standard',
  cancelTo = '/machines',
  onCancel,
  afterCreatePath = (machineId) => `/machines/${machineId}/conversations/new`,
}: MachineCreateFormProps) {
  const auth = useAuth();
  const navigate = useNavigate();
  const create = useCreateMachine();
  const facilitiesQuery = useFacilities();
  const select = useSelectMachine();
  const [form, setForm] = useState(emptyForm);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [duplicate, setDuplicate] = useState<PossibleDuplicateDto | null>(null);
  const idempotencyKey = useRef(newIdempotencyKey());
  const facilityIds = auth.activeMembership?.facility_ids ?? [];
  const facilities = facilitiesQuery.data?.facilities ?? [];
  const [facilityId, setFacilityId] = useState<string>(facilityIds.length === 1 ? facilityIds[0] : '');
  const firstMachine = variant === 'first-machine';

  const facilityHelp = useMemo(() => {
    if (facilityIds.length === 0) return facilities.length ? 'Optional. Assign this machine to a facility or leave it organization-wide.' : 'No facilities are configured yet. This machine will be organization-wide.';
    if (facilityIds.length === 1) return 'This machine will use your assigned facility scope.';
    return 'Choose one of the facilities available to your membership.';
  }, [facilities.length, facilityIds.length]);

  function update<K extends keyof typeof emptyForm>(key: K, value: (typeof emptyForm)[K]) {
    idempotencyKey.current = newIdempotencyKey();
    setForm((current) => ({ ...current, [key]: value }));
    setFieldErrors((current) => {
      const aliases: Partial<Record<keyof typeof emptyForm, string>> = {
        machineName: 'machine_name',
        machineType: 'machine_type',
        assetId: 'asset_id',
        productionArea: 'production_area',
        productionLine: 'production_line',
      };
      const apiKey = aliases[key];
      if (!current[key] && (!apiKey || !current[apiKey])) return current;
      const next = { ...current };
      delete next[key];
      if (apiKey) delete next[apiKey];
      return next;
    });
  }

  function updateFacility(value: string) {
    idempotencyKey.current = newIdempotencyKey();
    setFacilityId(value);
    setFieldErrors((current) => {
      if (!current.facility_id) return current;
      const next = { ...current };
      delete next.facility_id;
      return next;
    });
  }

  function payload(duplicateDecision?: 'CREATE_ANYWAY'): CreateMachineInput {
    return {
      facilityId: clean(facilityId),
      machineName: form.machineName.trim(),
      machineType: clean(form.machineType),
      assetId: clean(form.assetId),
      manufacturer: clean(form.manufacturer),
      model: clean(form.model),
      location: clean(form.location),
      productionArea: clean(form.productionArea),
      productionLine: clean(form.productionLine),
      aliases: form.aliases
        .split(/[,\n]/)
        .map((alias) => alias.trim())
        .filter(Boolean),
      duplicateDecision,
    };
  }

  function finishFirstMachineCreation() {
    if (firstMachine && auth.activeCustomerId) {
      clearFirstMachineActivationSession(auth.activeCustomerId);
    }
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setDuplicate(null);
    setFieldErrors({});
    const validationErrors: FieldErrors = {};
    if (!form.machineName.trim()) validationErrors.machineName = 'Machine name is required.';
    if (facilityIds.length > 1 && !facilityId) validationErrors.facility_id = 'Choose a facility for this machine.';
    if (Object.keys(validationErrors).length) {
      setFieldErrors(validationErrors);
      return;
    }
    try {
      const result = await create.mutateAsync({ input: payload(), idempotencyKey: idempotencyKey.current });
      if (isPossibleDuplicate(result)) {
        setDuplicate(result);
        return;
      }
      finishFirstMachineCreation();
      navigate(afterCreatePath(result.machine.machine_id), { replace: true });
    } catch (error) {
      setFieldErrors(apiFieldErrors(error));
    }
  }

  async function createAnyway() {
    try {
      const result = await create.mutateAsync({
        input: payload('CREATE_ANYWAY'),
        idempotencyKey: idempotencyKey.current,
      });
      if (isPossibleDuplicate(result)) {
        setDuplicate(result);
        return;
      }
      finishFirstMachineCreation();
      navigate(afterCreatePath(result.machine.machine_id), { replace: true });
    } catch (error) {
      setFieldErrors(apiFieldErrors(error));
      setDuplicate(null);
    }
  }

  async function useExisting(machineId: string) {
    const result = await select.mutateAsync(machineId);
    navigate(`/machines/${result.machine.machine_id}`, { replace: true });
  }

  const requestError = create.error instanceof Error ? create.error.message : null;
  const selectError = select.error instanceof Error ? select.error.message : null;
  const busy = create.isPending || select.isPending;

  const facilityField = (
    <Field label="Facility" required={facilityIds.length > 1} help={facilityHelp} error={fieldErrors.facility_id}>
      {facilityIds.length === 1 ? (
        <input value={facilities.find((facility) => facility.facility_id === facilityId)?.facility_name ?? facilityId} readOnly aria-readonly="true" />
      ) : facilities.length ? (
        <select value={facilityId} onChange={(event) => updateFacility(event.target.value)}>
          <option value="">{facilityIds.length > 0 ? 'Select facility' : 'No facility — organization-wide'}</option>
          {facilities.map((facility) => <option value={facility.facility_id} key={facility.facility_id}>{facility.facility_name}{facility.location ? ` — ${facility.location}` : ''}</option>)}
        </select>
      ) : (
        <div className="r42-field-note"><i className="bi bi-building" aria-hidden="true" /><span>Organization-wide</span></div>
      )}
    </Field>
  );

  return (
    <>
      <form className={`r42-machine-form${firstMachine ? ' r42-machine-form--first' : ''}`} onSubmit={(event) => void submit(event)} noValidate>
        {(requestError || selectError) && !duplicate ? (
          <div className="r42-form-alert" role="alert">
            <i className="bi bi-exclamation-circle" aria-hidden="true" />
            <div><strong>Route42 couldn't complete the machine setup.</strong><span>{selectError ?? requestError}</span></div>
          </div>
        ) : null}

        {firstMachine ? (
          <>
            <div className="r42-form-section r42-first-machine-minimum">
              <div className="r42-form-section__head">
                <h2>What machine should Route42 learn about?</h2>
                <p>Start with the name your team already uses. You can add the rest now or later.</p>
              </div>
              <div className={`r42-form-grid${facilityIds.length > 1 ? '' : ' r42-form-grid--single'}`}>
                <Field label="Machine name" required error={fieldErrors.machineName ?? fieldErrors.machine_name} help="Examples: Line 2 Conveyor, CNC-04, Boiler 3, NHVf Pump">
                  <input
                    autoFocus
                    maxLength={150}
                    value={form.machineName}
                    onChange={(event) => update('machineName', event.target.value)}
                    placeholder="e.g. Line 2 Conveyor"
                    aria-invalid={Boolean(fieldErrors.machineName ?? fieldErrors.machine_name)}
                  />
                </Field>
                {facilityIds.length > 1 ? facilityField : null}
              </div>
            </div>

            <details className="r42-form-section r42-form-section--details">
              <summary>Add machine details <span>Optional</span></summary>
              <div className="r42-form-grid r42-form-grid--details">
                <Field label="Machine type" error={fieldErrors.machine_type}>
                  <input maxLength={150} value={form.machineType} onChange={(event) => update('machineType', event.target.value)} placeholder="e.g. Centrifugal pump" />
                </Field>
                <Field label="Manufacturer" error={fieldErrors.manufacturer}>
                  <input maxLength={150} value={form.manufacturer} onChange={(event) => update('manufacturer', event.target.value)} placeholder="e.g. Hydro-Vacuum S.A." />
                </Field>
                <Field label="Model" error={fieldErrors.model}>
                  <input maxLength={150} value={form.model} onChange={(event) => update('model', event.target.value)} placeholder="e.g. NHVf" />
                </Field>
                <Field label="Asset ID" error={fieldErrors.asset_id}>
                  <input maxLength={100} value={form.assetId} onChange={(event) => update('assetId', event.target.value)} placeholder="e.g. CV-017" />
                </Field>
                {facilityIds.length <= 1 ? facilityField : null}
                <Field label="Location" error={fieldErrors.location}>
                  <input maxLength={250} value={form.location} onChange={(event) => update('location', event.target.value)} placeholder="e.g. Building A" />
                </Field>
                <Field label="Production area" error={fieldErrors.production_area}>
                  <input maxLength={150} value={form.productionArea} onChange={(event) => update('productionArea', event.target.value)} placeholder="e.g. Pump room" />
                </Field>
                <Field label="Production line" error={fieldErrors.production_line}>
                  <input maxLength={150} value={form.productionLine} onChange={(event) => update('productionLine', event.target.value)} placeholder="e.g. Line 2" />
                </Field>
                <Field label="Aliases" help="Separate aliases with commas or new lines." error={fieldErrors.aliases}>
                  <textarea rows={3} value={form.aliases} onChange={(event) => update('aliases', event.target.value)} placeholder="NHVf Series Pump, Vacuum Pump" />
                </Field>
              </div>
            </details>
          </>
        ) : (
          <>
            <div className="r42-form-section">
              <div className="r42-form-section__head">
                <h2>Machine identity</h2>
                <p>Start small. You can add more operational context later.</p>
              </div>
              <div className="r42-form-grid">
                <Field label="Machine name" required error={fieldErrors.machineName ?? fieldErrors.machine_name}>
                  <input
                    autoFocus
                    maxLength={150}
                    value={form.machineName}
                    onChange={(event) => update('machineName', event.target.value)}
                    placeholder="e.g. NHVf Pump"
                    aria-invalid={Boolean(fieldErrors.machineName ?? fieldErrors.machine_name)}
                  />
                </Field>
                <Field label="Machine type" error={fieldErrors.machine_type}>
                  <input maxLength={150} value={form.machineType} onChange={(event) => update('machineType', event.target.value)} placeholder="e.g. Centrifugal pump" />
                </Field>
                <Field label="Manufacturer" error={fieldErrors.manufacturer}>
                  <input maxLength={150} value={form.manufacturer} onChange={(event) => update('manufacturer', event.target.value)} placeholder="e.g. Hydro-Vacuum S.A." />
                </Field>
                <Field label="Model" error={fieldErrors.model}>
                  <input maxLength={150} value={form.model} onChange={(event) => update('model', event.target.value)} placeholder="e.g. NHVf" />
                </Field>
                <Field label="Asset ID" error={fieldErrors.asset_id}>
                  <input maxLength={100} value={form.assetId} onChange={(event) => update('assetId', event.target.value)} placeholder="e.g. CV-017" />
                </Field>
                {facilityField}
              </div>
            </div>

            <details className="r42-form-section r42-form-section--details">
              <summary>Additional machine context <span>Optional</span></summary>
              <div className="r42-form-grid r42-form-grid--details">
                <Field label="Location" error={fieldErrors.location}>
                  <input maxLength={250} value={form.location} onChange={(event) => update('location', event.target.value)} placeholder="e.g. Building A" />
                </Field>
                <Field label="Production area" error={fieldErrors.production_area}>
                  <input maxLength={150} value={form.productionArea} onChange={(event) => update('productionArea', event.target.value)} placeholder="e.g. Pump room" />
                </Field>
                <Field label="Production line" error={fieldErrors.production_line}>
                  <input maxLength={150} value={form.productionLine} onChange={(event) => update('productionLine', event.target.value)} placeholder="e.g. Line 2" />
                </Field>
                <Field label="Aliases" help="Separate aliases with commas or new lines." error={fieldErrors.aliases}>
                  <textarea rows={3} value={form.aliases} onChange={(event) => update('aliases', event.target.value)} placeholder="NHVf Series Pump, Vacuum Pump" />
                </Field>
              </div>
            </details>
          </>
        )}

        <div className="r42-form-actions">
          {onCancel ? (
            <button className="btn btn-outline-secondary" type="button" onClick={onCancel} disabled={busy}>Back</button>
          ) : (
            <Link className="btn btn-outline-secondary" to={cancelTo}>Cancel</Link>
          )}
          <button className="btn r42-btn-primary" type="submit" disabled={busy}>
            {create.isPending ? <span className="spinner-border spinner-border-sm" aria-hidden="true" /> : <i className="bi bi-plus-lg" aria-hidden="true" />}
            {create.isPending ? 'Creating…' : firstMachine ? 'Create first machine' : 'Create machine'}
          </button>
        </div>
      </form>

      {duplicate ? (
        <div className="r42-dialog-layer" role="dialog" aria-modal="true" aria-labelledby="duplicate-title">
          <div className="r42-dialog-backdrop" />
          <div className="r42-dialog-card">
            <span className="r42-eyebrow">Possible duplicate</span>
            <h2 id="duplicate-title">A similar machine already exists</h2>
            <p>Route42 found a machine with a matching name or asset identifier. Use the existing machine unless you intentionally need a separate record.</p>
            {selectError ? <div className="r42-form-alert" role="alert"><i className="bi bi-exclamation-circle" aria-hidden="true" /><div><strong>Route42 couldn't open the existing machine.</strong><span>{selectError}</span></div></div> : null}
            <div className="r42-duplicate-list">
              {duplicate.possible_matches.map((match) => (
                <button type="button" key={match.machine_id} onClick={() => void useExisting(match.machine_id)} disabled={busy}>
                  <span><strong>{match.machine_name}</strong><small>{match.asset_id || match.machine_id}</small></span>
                  <span>Use existing <i className="bi bi-arrow-right" aria-hidden="true" /></span>
                </button>
              ))}
            </div>
            <div className="r42-dialog-actions">
              <button type="button" className="btn btn-outline-secondary" onClick={() => setDuplicate(null)} disabled={busy}>Review details</button>
              {duplicate.allowed_actions.includes('CREATE_ANYWAY') ? (
                <button type="button" className="btn r42-btn-primary" onClick={() => void createAnyway()} disabled={busy}>Create separate machine</button>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

function Field({
  label,
  required = false,
  error,
  help,
  children,
}: {
  label: string;
  required?: boolean;
  error?: string;
  help?: string;
  children: ReactNode;
}) {
  return (
    <label className="r42-field">
      <span className="r42-field__label">{label}{required ? <b>Required</b> : null}</span>
      {children}
      {error ? <small className="r42-field__error">{error}</small> : help ? <small>{help}</small> : null}
    </label>
  );
}
